import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  DndContext,
  PointerSensor,
  closestCorners,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import { toast } from 'sonner';
import { AlertTriangle, KanbanSquare, Plus } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { EmptyState } from '@/components/ui/empty-state';
import { PageHeader } from '@/components/ui/page-header';
import { useEntreprises } from '@/hooks/queries/useEntreprises';
import { useTiers } from '@/hooks/queries/useTiers';
import { useUsersList } from '@/hooks/queries/useUsersList';
import { usePipelineEtapes, usePipelines } from '@/hooks/queries/useCrmPipelines';
import { useChangeEtape, useOpportunites } from '@/hooks/queries/useCrmOpportunites';
import { formatDA, formatDateDZ } from '@/lib/format';
import { cn } from '@/lib/utils';
import type { OpportuniteRow } from '@/lib/database.types';
import { OpportuniteFormDialog } from './OpportuniteFormDialog';

export function CrmKanbanPage(): JSX.Element {
  const { data: entreprises = [] } = useEntreprises();
  const [entrepriseId, setEntrepriseId] = useState('');
  useEffect(() => {
    if (!entrepriseId && entreprises[0]) setEntrepriseId(entreprises[0].id);
  }, [entreprises, entrepriseId]);

  const { data: pipelines = [] } = usePipelines(entrepriseId);
  const [searchParams, setSearchParams] = useSearchParams();
  const queryPipelineId = searchParams.get('pipeline') ?? '';
  const [pipelineId, setPipelineId] = useState(queryPipelineId);
  const [createOpen, setCreateOpen] = useState(false);

  useEffect(() => {
    if (!pipelineId && pipelines[0]) {
      setPipelineId(pipelines[0].id);
      setSearchParams({ pipeline: pipelines[0].id }, { replace: true });
    }
  }, [pipelines, pipelineId, setSearchParams]);

  const { data: etapes = [] } = usePipelineEtapes(pipelineId || null);

  const { data: oppsData } = useOpportunites({
    entrepriseId,
    pipelineId: pipelineId || null,
    statut: 'open',
    pageSize: 200,
  });
  const opportunites = useMemo(() => oppsData?.rows ?? [], [oppsData]);

  const { data: tiers = [] } = useTiers({ entrepriseId, search: '' });
  const tierMap = useMemo(() => {
    const m = new Map<string, string>();
    for (const tt of tiers) m.set(tt.id, tt.raison_sociale);
    return m;
  }, [tiers]);

  const { data: users = [] } = useUsersList();
  const userMap = useMemo(() => {
    const m = new Map<string, string>();
    for (const u of users) m.set(u.user_id, u.full_name ?? u.user_id.slice(0, 8));
    return m;
  }, [users]);

  const oppsByEtape = useMemo(() => {
    const m = new Map<string, OpportuniteRow[]>();
    for (const e of etapes) m.set(e.id, []);
    for (const o of opportunites) {
      const arr = m.get(o.etape_id);
      if (arr) arr.push(o);
    }
    return m;
  }, [etapes, opportunites]);

  const changeEtape = useChangeEtape();

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const handleDragEnd = (event: DragEndEvent): void => {
    const { active, over } = event;
    if (!over) return;
    const oppId = String(active.id);
    const newEtapeId = String(over.id);
    const opp = opportunites.find((o) => o.id === oppId);
    if (!opp || opp.etape_id === newEtapeId) return;
    void changeEtape
      .mutateAsync({ id: oppId, etape_id: newEtapeId })
      .then(() => {
        const dest = etapes.find((e) => e.id === newEtapeId);
        toast.success(`Déplacée vers « ${dest?.nom ?? newEtapeId} »`);
      })
      .catch((e: Error) => toast.error(e.message));
  };

  const totalKanban = opportunites.length;
  const overflow = totalKanban >= 200;
  const noPipeline = entrepriseId && pipelines.length === 0;

  return (
    <div className="flex h-full flex-col">
      <div className="space-y-3 border-b border-border bg-card p-4">
        <PageHeader
          title="Kanban"
          subtitle={
            overflow
              ? '⚠️ 200 opportunités max chargées — affinez les filtres pour voir plus.'
              : undefined
          }
          actions={
            <div className="flex flex-wrap items-center gap-2">
              <select
                value={entrepriseId}
                onChange={(e) => setEntrepriseId(e.target.value)}
                className="h-9 rounded-md border border-input bg-background px-2 text-sm"
              >
                {entreprises.map((e) => (
                  <option key={e.id} value={e.id}>
                    {e.nom}
                  </option>
                ))}
              </select>
              <select
                value={pipelineId}
                onChange={(e) => {
                  setPipelineId(e.target.value);
                  setSearchParams({ pipeline: e.target.value });
                }}
                className="h-9 rounded-md border border-input bg-background px-2 text-sm"
              >
                {pipelines.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.nom}
                  </option>
                ))}
              </select>
              <Button onClick={() => setCreateOpen(true)} disabled={!!noPipeline}>
                <Plus className="h-4 w-4" /> Nouvelle opportunité
              </Button>
            </div>
          }
        />
      </div>

      <div className="scrollbar-thin flex-1 overflow-x-auto overflow-y-hidden bg-secondary/20 p-4">
        {noPipeline ? (
          <EmptyState
            icon={<KanbanSquare className="h-6 w-6" />}
            title={`Aucun pipeline configuré pour ${entreprises.find((e) => e.id === entrepriseId)?.nom ?? 'cette entreprise'}`}
            description="Créez votre premier pipeline pour commencer à suivre vos opportunités commerciales."
            action={
              <Button asChild>
                <Link to={`/parametres/crm/pipelines?entreprise=${entrepriseId}`}>
                  → Configurer un pipeline
                </Link>
              </Button>
            }
          />
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCorners}
            onDragEnd={handleDragEnd}
          >
            <div className="flex h-full min-h-[400px] gap-3">
              {etapes.map((etape) => {
                const opps = oppsByEtape.get(etape.id) ?? [];
                const totalEtape = opps.reduce((sum, o) => sum + Number(o.montant_estime_dzd), 0);
                return (
                  <KanbanColumn
                    key={etape.id}
                    etapeId={etape.id}
                    nom={etape.nom}
                    emoji={etape.icone_emoji ?? ''}
                    couleur={etape.couleur ?? '#94a3b8'}
                    count={opps.length}
                    total={totalEtape}
                  >
                    {opps.slice(0, 50).map((o) => (
                      <KanbanCard
                        key={o.id}
                        opp={o}
                        tierName={o.tier_id ? tierMap.get(o.tier_id) : undefined}
                        commercialName={o.commercial_id ? userMap.get(o.commercial_id) : undefined}
                      />
                    ))}
                    {opps.length > 50 && (
                      <div className="rounded-md border border-warning/40 bg-warning/10 p-2 text-[11px] text-warning">
                        +{opps.length - 50} non affichées — utilisez la Liste pour la suite.
                      </div>
                    )}
                  </KanbanColumn>
                );
              })}
            </div>
          </DndContext>
        )}
      </div>

      <OpportuniteFormDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        entrepriseId={entrepriseId}
        editing={null}
        defaultPipelineId={pipelineId}
        onSaved={() => setCreateOpen(false)}
      />
    </div>
  );
}

function KanbanColumn({
  etapeId,
  nom,
  emoji,
  couleur,
  count,
  total,
  children,
}: {
  etapeId: string;
  nom: string;
  emoji: string;
  couleur: string;
  count: number;
  total: number;
  children: React.ReactNode;
}): JSX.Element {
  const { setNodeRef, isOver } = useDroppable({ id: etapeId });
  return (
    <div
      ref={setNodeRef}
      className={cn(
        'flex h-full w-72 shrink-0 flex-col rounded-lg border bg-card transition-colors',
        isOver ? 'border-primary' : 'border-border',
      )}
    >
      <div
        className="flex items-center justify-between border-b border-border px-3 py-2 text-xs"
        style={{ borderTop: `3px solid ${couleur}` }}
      >
        <div className="flex items-center gap-1.5 font-semibold">
          <span>{emoji}</span>
          <span>{nom}</span>
        </div>
        <div className="text-right">
          <div className="text-muted-foreground">{count}</div>
          <div className="num text-[10px]">{formatDA(total)}</div>
        </div>
      </div>
      <div className="scrollbar-thin flex-1 space-y-2 overflow-y-auto p-2">{children}</div>
    </div>
  );
}

function KanbanCard({
  opp,
  tierName,
  commercialName,
}: {
  opp: OpportuniteRow;
  tierName?: string;
  commercialName?: string;
}): JSX.Element {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({ id: opp.id });
  const style = transform
    ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)` }
    : undefined;

  // Couleur card : rouge si > 14j sans activité, jaune si proche cloture_prevue
  const lastActiv = opp.derniere_activite_at
    ? new Date(opp.derniere_activite_at).getTime()
    : new Date(opp.date_creation).getTime();
  const daysSince = Math.floor((Date.now() - lastActiv) / 86400000);
  const stale = daysSince > 14;

  const closeIn = opp.date_cloture_prevue
    ? Math.floor((new Date(opp.date_cloture_prevue).getTime() - Date.now()) / 86400000)
    : null;
  const deadlineSoon = closeIn !== null && closeIn >= 0 && closeIn <= 7;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        'cursor-grab rounded-md border bg-background p-2 text-xs shadow-sm transition active:cursor-grabbing',
        isDragging && 'opacity-50',
        stale ? 'border-destructive/60' : deadlineSoon ? 'border-warning/60' : 'border-border',
      )}
      {...listeners}
      {...attributes}
    >
      <Link
        to={`/crm/opportunite/${opp.id}`}
        className="line-clamp-2 font-medium hover:underline"
        onClick={(e) => e.stopPropagation()}
      >
        {opp.titre}
      </Link>
      <div className="mt-1 flex items-center justify-between text-[10px] text-muted-foreground">
        <span className="font-mono">{opp.reference}</span>
        <span className="num font-semibold text-foreground">
          {formatDA(Number(opp.montant_estime_dzd))}
        </span>
      </div>
      <div className="mt-1 flex flex-wrap items-center gap-1 text-[10px] text-muted-foreground">
        {tierName && <span className="line-clamp-1">{tierName}</span>}
        {commercialName && <Badge variant="outline">{commercialName}</Badge>}
        {opp.date_cloture_prevue && (
          <span className="num">{formatDateDZ(opp.date_cloture_prevue)}</span>
        )}
        {stale && (
          <span className="inline-flex items-center gap-0.5 text-destructive">
            <AlertTriangle className="h-2.5 w-2.5" />
            {daysSince}j
          </span>
        )}
      </div>
    </div>
  );
}
